import React, { useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Pencil } from "lucide-react";
import { LAYOUTS } from "./layouts";
import designerConfig from "../utils/designerConfig.json";
import "./PosterGallery.css";

function resolveCssVars(design, customThemes = []) {
  const themeId = design?.activeTheme    || "light-studio";
  const fontId  = design?.activeFontPair || "modern-clean";
  
  // First try to find in default themes
  let theme = designerConfig.themes.find((t) => t.id === themeId);
  
  // If not found and it's a custom theme, find in custom themes
  if (!theme && customThemes.length > 0) {
    theme = customThemes.find((t) => t.id === themeId);
  }
  
  // Fallback to first default theme
  if (!theme) {
    theme = designerConfig.themes[0];
  }
  
  const font = designerConfig.fontPairs.find((f) => f.id === fontId) || designerConfig.fontPairs[0];
  
  return {
    "--theme-bg":           theme.colors.bg,
    "--theme-surface":      theme.colors.surface,
    "--theme-text":         theme.colors.text,
    "--theme-textMuted":    theme.colors.textMuted,
    "--theme-border":       theme.colors.border,
    "--theme-accent":       theme.colors.accent,
    "--theme-primary":      theme.colors.primary,
    "--theme-heading-font": `"${font?.heading || "Montserrat"}", sans-serif`,
    "--theme-body-font":    `"${font?.body    || "Open Sans"}", sans-serif`,
  };
}

const FALLBACK = {
  activeTheme:    "light-studio",
  activeFontPair: "modern-clean",
  activeLayout:   "editorial-catalogue",
  visibleFields:  {},
};

const PosterGallery = ({ buyer, products }) => {
  const navigate   = useNavigate();
  const rowRef     = useRef(null);
  const touchedRef = useRef(false);


  useEffect(() => {
    const el = rowRef.current;
    if (!el) return;
    const t = setTimeout(() => {
      if (!touchedRef.current)
        el.scrollTo({ left: el.scrollWidth - el.clientWidth, behavior: "smooth" });
    }, 10000);
    const mark = () => { touchedRef.current = true; };
    el.addEventListener("scroll",     mark, { passive: true });
    el.addEventListener("touchstart", mark, { passive: true });
    return () => { clearTimeout(t); el.removeEventListener("scroll", mark); el.removeEventListener("touchstart", mark); };
  }, []);

  const posterDesigns = buyer?.posterDesigns || {};
  const sorted = [...products].sort((a, b) =>
    (!!posterDesigns[b.segment]) - (!!posterDesigns[a.segment])
  );

  return (
    <div className="pg-section">
      <div className="pg-header">
        <h2>🖼 Poster Designs</h2>
        <p>Tap any poster to open Visual Designer</p>
      </div>

      <div className="pg-row" ref={rowRef}>
        {sorted.map((product) => {
          const seg     = product.segment;
          const design  = posterDesigns[seg] || FALLBACK;
          const Layout  = LAYOUTS[design?.activeLayout || "editorial-catalogue"]
                       || LAYOUTS["editorial-catalogue"];
          const cssVars = resolveCssVars(design);
          const visible = design?.visibleFields || {};
          const appliedTheme = design?.appliedTheme;
          const isCustomTheme = appliedTheme && appliedTheme.id.startsWith("custom-");

          return (
            <div
              key={product.id}
              className="pg-card"
              onClick={() => navigate(`/visual-designer/${seg}`, { state: { product } })}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === "Enter" && navigate(`/visual-designer/${seg}`, { state: { product } })}
              aria-label={`Edit ${product.name} poster`}
            >
              {/* poster preview */}
              <div className="pg-preview">
                <div className="pg-scaler">
                  <Layout
                    product={product}
                    buyer={buyer}
                    cardScale={100}
                    visibleFields={visible}
                    cssVars={cssVars}
                    forExport={false}
                  />
                </div>

                {/* Applied Theme Badge */}
                {isCustomTheme && (
                  <div className="pg-theme-badge">
                    <span className="badge-icon">✨</span>
                    <span className="badge-text">{appliedTheme.name}</span>
                  </div>
                )}

                {/* hover overlay */}
                <div className="pg-overlay" aria-hidden="true">
                  <div className="pg-edit-pill">
                    <Pencil size={14} />
                    <span>Edit</span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PosterGallery;
